import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { BitacoraCliente } from './MigracionOltBitacora';
import type {
  IMigracionOltCorrida,
  IMigracionOltResultadoMigracion,
} from '@/shared/actions/erp/tickets-soporte-cliente/softnet-import.actions';

/**
 * La corrida que está ejecutando el servidor, como panel desplegable.
 *
 * Existe para que recargar la página no sea perder el hilo: el proceso no vive
 * en la pantalla, así que al volver hay que poder ver dónde está sin repetir
 * los pasos 1 a 4. Por eso se pinta arriba de todo y por su cuenta, no dentro
 * del paso 4 —que tras recargar no está—.
 *
 * Se despliega y se repliega porque son dos necesidades distintas: de un
 * vistazo basta el título con la barra y los controles, y el detalle (cliente
 * por cliente) sólo cuando se quiere mirar. Lo que nunca se esconde es el
 * avance ni el botón de detener: son lo que se busca con urgencia.
 *
 * Si no hay corrida no pinta nada: ni vacíos ni avisos. No tener una corrida
 * andando es el estado normal de la pantalla.
 */

const CFG: Record<string, { titulo: string; color: 'info' | 'warning' | 'success' | 'error' }> = {
  EN_CURSO: { titulo: 'Migración en proceso', color: 'info' },
  PAUSADA: { titulo: 'Migración pausada', color: 'warning' },
  TERMINADA: { titulo: 'Migración terminada', color: 'success' },
  CANCELADA: { titulo: 'Migración detenida', color: 'error' },
};

const hora = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
};

interface Props {
  corrida: IMigracionOltCorrida | null;
  /** Lo que ya se migró, leído de la base. */
  bitacora: IMigracionOltResultadoMigracion[];
  /** Sin permiso de ejecución se ve pero no se controla. */
  puedeControlar?: boolean;
  onPausar: () => void;
  onReanudar: () => void;
  onCancelar: () => void;
  /** Mientras una acción está en vuelo. */
  ocupado?: boolean;
  /**
   * Si pintar también los clientes. Se apaga cuando el paso 4 está en pantalla,
   * que ya los muestra: dos veces la misma lista confunde.
   */
  mostrarClientes?: boolean;
  onReintentar?: (item: IMigracionOltResultadoMigracion) => void;
  serieReintentando?: string | null;
}

const MigracionOltCorridaPanel: React.FC<Props> = ({
  corrida,
  bitacora,
  puedeControlar = false,
  onPausar,
  onReanudar,
  onCancelar,
  ocupado = false,
  mostrarClientes = false,
  onReintentar,
  serieReintentando,
}) => {
  /**
   * Los hooks van ANTES del return temprano: cuando aparece una corrida el
   * componente pasa de no pintar nada a pintar, y si los hooks estuvieran
   * después React se encontraría con otra cantidad en cada render.
   */
  const viva = corrida?.estado === 'EN_CURSO' || corrida?.estado === 'PAUSADA';
  const [abiertoManual, setAbiertoManual] = useState<boolean | null>(null);
  /** Detener pide confirmación: no se corta un proceso sobre equipos sin querer. */
  const [confirmandoDetener, setConfirmandoDetener] = useState(false);

  if (!corrida) return null;

  // Mientras trabaja se abre solo; terminada arranca replegada, que es cuando
  // el detalle ya no urge. El usuario manda sobre eso en cuanto toca la flecha.
  const abierto = abiertoManual ?? viva;
  const cfg = CFG[corrida.estado] ?? { titulo: 'Migración', color: 'info' as const };
  const total = corrida.total || 0;
  const procesados = corrida.procesados || 0;
  const pct = total > 0 ? (procesados / total) * 100 : 0;

  const detener = () => {
    setConfirmandoDetener(false);
    onCancelar();
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 2 }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Tooltip arrow title={abierto ? 'Ocultar el detalle' : 'Ver el detalle'}>
          <IconButton
            size="small"
            onClick={() => setAbiertoManual(!abierto)}
            aria-label={abierto ? 'Ocultar el detalle de la migración' : 'Ver el detalle de la migración'}
            aria-expanded={abierto}
            sx={{
              transform: abierto ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform .2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Tooltip>

        {corrida.estado === 'EN_CURSO' ? (
          <CircularProgress size={20} />
        ) : corrida.estado === 'TERMINADA' ? (
          <CheckCircleIcon color="success" />
        ) : corrida.estado === 'PAUSADA' ? (
          <PauseIcon color="warning" />
        ) : null}

        <Typography variant="subtitle1" fontWeight={700}>
          {cfg.titulo}
        </Typography>

        <Chip
          size="small"
          variant="outlined"
          label={`${corrida.ip_olt}${
            corrida.nombre_olt && corrida.nombre_olt !== 'null' ? ` · ${corrida.nombre_olt}` : ''
          }`}
        />
        {corrida.archivo_excel && (
          <Tooltip arrow title="Excel cargado en esta corrida; el archivo no se guarda">
            <Chip size="small" variant="outlined" label={corrida.archivo_excel} />
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {procesados} de {total}
          {hora(corrida.fecha_creacion) ? ` · desde las ${hora(corrida.fecha_creacion)}` : ''}
          {corrida.us_creacion ? ` · ${corrida.us_creacion}` : ''}
        </Typography>

        {/* Los controles quedan FUERA del Collapse: detener una migración no
            puede depender de que el panel esté desplegado. */}
        {puedeControlar && corrida.estado === 'EN_CURSO' && !confirmandoDetener && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PauseIcon />}
              onClick={onPausar}
              disabled={ocupado}
              sx={{ textTransform: 'none', minHeight: 44 }}
            >
              {ocupado ? 'Pausando...' : 'Pausar'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={() => setConfirmandoDetener(true)}
              disabled={ocupado}
              sx={{ textTransform: 'none', minHeight: 44 }}
            >
              Detener
            </Button>
          </>
        )}
        {puedeControlar && corrida.estado === 'EN_CURSO' && confirmandoDetener && (
          <>
            <Typography variant="caption" color="error.main" fontWeight={600}>
              ¿Detener? Los pendientes no se migran.
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="error"
              disableElevation
              onClick={detener}
              disabled={ocupado}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
            >
              Sí, detener
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => setConfirmandoDetener(false)}
              sx={{ textTransform: 'none', minHeight: 44 }}
            >
              Seguir migrando
            </Button>
          </>
        )}
        {puedeControlar && corrida.estado === 'PAUSADA' && (
          <>
            <Button
              size="small"
              variant="contained"
              color="warning"
              disableElevation
              startIcon={<RouterIcon />}
              onClick={onReanudar}
              disabled={ocupado}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
            >
              Reanudar donde quedó
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={detener}
              disabled={ocupado}
              sx={{ textTransform: 'none', minHeight: 44 }}
            >
              Abandonar
            </Button>
          </>
        )}
      </Stack>

      {/* La barra tampoco se esconde: replegado, el panel sigue sirviendo para
          saber de un vistazo por dónde va. */}
      {viva && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress
            variant={total > 0 ? 'determinate' : 'indeterminate'}
            value={pct}
            color={cfg.color === 'info' ? 'primary' : cfg.color}
            sx={{ height: 8, borderRadius: 1 }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {corrida.pendientes} pendiente(s) · {corrida.hechos} hecho(s)
              {corrida.omitidos > 0 ? ` · ${corrida.omitidos} no se pudieron procesar` : ''}
            </Typography>
            {corrida.en_curso_serie && (
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                migrando ahora: {corrida.en_curso_serie}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      <Collapse in={abierto} timeout="auto" unmountOnExit>
        {corrida.estado === 'EN_CURSO' && (
          <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0.5 }}>
            Lo está ejecutando el servidor: puedes recargar, cerrar la página o entrar desde otra
            máquina y el proceso sigue. Al volver vas a ver el avance donde esté. Si lo detienes, se
            termina el cliente que está en curso y ahí para: ninguno queda a medias.
          </Alert>
        )}
        {corrida.estado === 'PAUSADA' && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 1.5, py: 0.5 }}>
            Se terminó el cliente que estaba en curso y ahí paró. Al reanudar sigue desde el
            siguiente pendiente; nadie quedó a medias.
          </Alert>
        )}
        {!viva && corrida.mensaje && (
          <Alert
            severity={
              corrida.estado === 'CANCELADA'
                ? 'warning'
                : corrida.omitidos > 0
                  ? 'warning'
                  : 'success'
            }
            variant="outlined"
            sx={{ mt: 1.5, py: 0.5 }}
          >
            {corrida.mensaje}
            {corrida.estado === 'CANCELADA' && corrida.pendientes > 0
              ? ` Quedaron ${corrida.pendientes} cliente(s) sin migrar.`
              : ''}
          </Alert>
        )}

        {mostrarClientes && bitacora.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Clientes de esta corrida
            </Typography>
            {bitacora.map((b) => (
              <BitacoraCliente
                key={`${b.serie}-${b.id_ejecucion ?? ''}`}
                item={b}
                onReintentar={onReintentar}
                reintentando={serieReintentando === b.serie}
              />
            ))}
          </Box>
        )}

        {mostrarClientes && bitacora.length === 0 && corrida.estado === 'EN_CURSO' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Todavía no terminó el primer cliente; en cuanto haya resultados aparecen acá.
          </Typography>
        )}
      </Collapse>
    </Paper>
  );
};

export default MigracionOltCorridaPanel;
